import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BRAND } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, BookOpen, Map as MapIcon, Flame,
  Users as UsersIcon, CalendarDays, ShieldCheck, LogOut, Menu, X, Video, Bell, LogIn, FileText
} from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/protocol",  label: "Daily Protocol", icon: Flame, testid: "nav-protocol" },
  { to: "/learn",     label: "Learning Hub", icon: BookOpen, testid: "nav-learn" },
  { to: "/book",      label: "Book Dr. Slava", icon: Video, testid: "nav-book" },
  { to: "/my-intake", label: "My Intake", icon: FileText, testid: "nav-my-intake" },
  { to: "/pathway",   label: "Pathway", icon: MapIcon, testid: "nav-pathway" },
  { to: "/community", label: "Community", icon: UsersIcon, testid: "nav-community" },
  { to: "/events",    label: "Live Events", icon: CalendarDays, testid: "nav-events" },
];

const guestNavItems = [
  { to: "/book-session", label: "Book a Session", icon: Video, testid: "nav-book-guest" },
  { to: "/pricing",      label: "Programs & Pricing", icon: LayoutDashboard, testid: "nav-pricing-guest" },
  { to: "/contact",      label: "Contact", icon: BookOpen, testid: "nav-contact-guest" },
];

export default function AppShell({ children, guest = false }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isGuest = guest || !user;
  const baseItems = isGuest ? guestNavItems : navItems;
  const items = [...baseItems];
  if (!isGuest && user?.is_admin) items.push({ to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" });

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const brandLink = isGuest ? "/" : "/dashboard";

  return (
    <div className="min-h-screen flex bg-[#050505] text-white">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-white/5 bg-[#070707] sticky top-0 h-screen">
        <Link to={brandLink} className="flex items-center gap-3 px-6 py-6" data-testid="brand-link">
          <img src={BRAND.logo} alt="Dr Thrive 365" className="h-10 w-10 rounded-lg object-cover" />
          <div>
            <div className="font-display text-xl leading-none">Dr Thrive 365</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1">Rewire · Restore</div>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-2 space-y-1">
          {items.map((it) => {
            const active = location.pathname.startsWith(it.to);
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  active
                    ? "bg-gradient-to-r from-[#FF8A00]/20 to-[#E52E71]/10 text-white border border-[#FF8A00]/30"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon size={18} className={active ? "text-[#FF8A00]" : ""} />
                <span>{it.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/5">
          {isGuest ? (
            <>
              <Link to="/login"
                data-testid="nav-guest-signin"
                className="w-full flex items-center justify-center gap-2 text-sm text-white py-2.5 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] mb-2"
              >
                <LogIn size={14} /> Sign in
              </Link>
              <Link to="/register"
                data-testid="nav-guest-register"
                className="w-full flex items-center justify-center gap-2 text-xs text-white/60 hover:text-white py-2 rounded-lg hover:bg-white/5"
              >
                Create account
              </Link>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center text-sm font-semibold">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate" data-testid="sidebar-user-name">{user?.name}</div>
                  <div className="text-[11px] text-white/40 truncate" data-testid="sidebar-user-email">{user?.email}</div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#FF8A00]">{user?.tier} tier</div>
                </div>
              </div>
              <Link
                to="/settings/notifications"
                data-testid="nav-notifications"
                className="w-full flex items-center justify-center gap-2 text-xs text-white/60 hover:text-white py-2 rounded-lg hover:bg-white/5 mb-1"
              >
                <Bell size={14} /> Notifications
              </Link>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full flex items-center justify-center gap-2 text-xs text-white/60 hover:text-white py-2 rounded-lg hover:bg-white/5"
              >
                <LogOut size={14} /> Sign out
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 flex items-center justify-between px-4 glass">
        <Link to={brandLink} className="flex items-center gap-2">
          <img src={BRAND.logo} alt="" className="h-8 w-8 rounded-md" />
          <span className="font-display text-lg">Dr Thrive 365</span>
        </Link>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 top-14 z-30 bg-[#050505]/95 p-4 space-y-1" onClick={() => setOpen(false)}>
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`mobile-${it.testid}`}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/5"
            >
              <it.icon size={18} /> {it.label}
            </Link>
          ))}
          {isGuest ? (
            <Link
              to="/login"
              data-testid="mobile-nav-guest-signin"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/5"
            >
              <LogIn size={18} /> Sign in
            </Link>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/60 hover:bg-white/5"
            >
              <LogOut size={18} /> Sign out
            </button>
          )}
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 lg:py-10">{children}</div>
        <footer className="px-4 sm:px-8 py-8 text-[11px] text-white/30 border-t border-white/5 mt-8">
          <p data-testid="disclaimer-footer">
            This content is for educational purposes only and not medical advice. Always consult a qualified
            healthcare provider before starting any protocol.
          </p>
        </footer>
      </main>
    </div>
  );
}
